import { motion } from 'framer-motion';
import {
  type Tier,
  formatGBPCompact,
  formatGBPRange,
  formatRange,
  tierTotals,
} from '@/lib/roi/data';

interface TierCardProps {
  tier: Tier;
  index: number;
}

export function TierCard({ tier, index }: TierCardProps) {
  const totals = tierTotals(tier);
  const hasRange = tier.casesPerYear.high !== undefined;
  const savingDisplay = hasRange
    ? formatGBPRange(totals.savingLow, totals.savingHigh)
    : formatGBPCompact(totals.savingLow);
  const wipDisplay = hasRange
    ? formatGBPRange(totals.wipLow, totals.wipHigh)
    : formatGBPCompact(totals.wipLow);

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.08,
      }}
      className="pitch-card flex flex-col gap-6 rounded-xl p-6"
    >
      <header className="flex flex-col gap-2">
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-ink-dim">
          {tier.label}
        </span>
        <p className="text-sm leading-snug text-ink-muted">{tier.profile}</p>
      </header>

      <div className="flex flex-col gap-2">
        <span className="text-[13px] font-medium text-ink-muted">
          Annual saving per firm
        </span>
        <span className="font-semibold tabular-nums text-brand-500 [font-size:48px] [line-height:1]">
          {savingDisplay}
        </span>
        <span className="text-xs text-ink-dim">
          Model assumption: Asset Hound saves {tier.timeSavingPercent}% of
          manual time, applied to annual WIP
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border-subtle pt-5 text-sm">
        <Metric label="Cases / year" value={formatRange(tier.casesPerYear)} />
        <Metric
          label="Avg. spend per case"
          value={formatGBPCompact(tier.spendPerCase)}
        />
        <Metric label="Annual WIP" value={wipDisplay} />
        <Metric
          label="Goes to plan"
          value={formatRange(tier.goingToPlanPercent, '%')}
        />
      </dl>

      {tier.chargeOutRates.length > 0 ? (
        <div className="flex flex-col gap-2 border-t border-border-subtle pt-5">
          <span className="text-[13px] font-medium text-ink-muted">
            Charge-out rates
          </span>
          <table className="w-full text-sm">
            <tbody>
              {tier.chargeOutRates.map((rate) => (
                <tr
                  key={rate.role}
                  className="border-b border-border-divider last:border-0"
                >
                  <td className="py-2 pr-3 text-ink-strong">{rate.role}</td>
                  <td className="py-2 pr-3 text-ink-muted">
                    {rate.hoursPerCase}
                  </td>
                  <td className="py-2 text-right tabular-nums text-ink-strong">
                    {rate.rate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <ul className="flex flex-col gap-2 border-t border-border-subtle pt-5 text-[13px] leading-relaxed text-ink-muted">
        {tier.notes.map((note) => (
          <li key={note} className="flex gap-2">
            <span
              aria-hidden
              className="mt-[7px] block h-1 w-1 flex-none rounded-full bg-brand-500"
            />
            <span>{note}</span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-ink-dim">{label}</dt>
      <dd className="text-sm font-medium tabular-nums text-ink-strong">
        {value}
      </dd>
    </div>
  );
}
